// WS-клиент: reconnect + добор по last-seq + поток ServerEvent. SPEC client-transport §4.3 / §4.4 / §4.9.
//
// Жизненный цикл (§4.3): connect (cookie-хендшейк, без токена в URL) → ready (сверка
// WS_PROTOCOL_VERSION; несовпадение мажора → protocol.outdated, сокет закрыт, reconnect НЕ крутим) →
// subscribe → sync с курсорами last-seq (на первом И каждом reconnect — добор дельты, инв.3) →
// диспетчеризация входящих ServerEvent наружу (через DedupGate) → реле эфемерных команд (read/typing).
// Reconnect: backoff+jitter, сброс attempt только после ready; реакция на online/offline; close 4401
// → auth.required. Команды до ready буферятся.
import { WS_PROTOCOL_VERSION } from '@tele-chat/shared-contracts';
import { backoffDelay } from './backoff.js';
import { DedupGate } from './event-hub.js';
import { WS_OPEN, } from './ports.js';
/** Close-код «сессия невалидна» — realtime-gateway рвёт сокет при отзыве/протухании (R2 K, §4.9). */
export const CLOSE_AUTH_REQUIRED = 4401;
/**
 * Дедлайн установления соединения open→ready (мс). Наблюдение client-diag 2026-07-05 (iPhone без
 * VPN): зависший WS-хендшейк честно ждал TCP-ретрансмиты 49 секунд, пока СВЕЖЕЕ соединение (проба
 * /healthz) проходило за 10мс — при эпизодической потере пакетов новая попытка (новый source-port)
 * почти всегда быстрее ожидания старой. Штатный handshake+ready укладывается в ~1.5с даже на 3G.
 */
export const DEFAULT_CONNECT_TIMEOUT_MS = 10_000;
/**
 * RealtimeClient: один сокет на приложение (D-05). Курсоры last-seq приходят снаружи через
 * setCursors (их держит client-store, R2 п.F) и шлются в `sync` после каждого subscribe.
 */
export class WsRealtimeClient {
    deps;
    dedup = new DedupGate();
    ws = null;
    _status = 'closed';
    attempt = 0;
    reconnectTimer = null;
    /** Таймер дедлайна установления соединения (open→ready). */
    connectTimer = null;
    /** Кандидаты эндпоинтов: [0] = primary (same-origin, cookie), дальше — фолбэки (тикет). */
    candidates = [];
    endpointIdx = 0;
    /** Реентерабельность open(): асинхронная подготовка (тикет) не должна плодить сокеты. */
    opening = false;
    stopped = true;
    ready = false;
    /** Команды, накопленные до `ready` (subscribe/sync/read/typing). SPEC §4.3.1. */
    preReadyBuffer = [];
    /** Курсоры last-seq от client-store (источник sync-добора). SPEC §4.3.4. */
    cursors = [];
    /** Узкий список чатов подписки (опционально); по умолчанию — все чаты участника. */
    subscribeChatIds;
    offOnline = null;
    offOffline = null;
    offVisible = null;
    constructor(deps) {
        this.deps = deps;
        this.candidates = [deps.url, ...(deps.fallbackUrls ?? [])];
    }
    get status() {
        return this._status;
    }
    /** Запускает соединение и reconnect-цикл; подписывается на online/offline. SPEC §4.1 start. */
    start() {
        if (!this.stopped)
            return;
        this.stopped = false;
        this.offOnline = this.deps.network.onOnline(() => this.onNetworkOnline());
        this.offOffline = this.deps.network.onOffline(() => this.onNetworkOffline());
        this.offVisible = this.deps.visibility?.onVisible(() => this.onPageVisible()) ?? null;
        if (this.deps.network.isOnline() && this.isPageVisible())
            this.open();
        else
            this.setStatus('closed'); // ждём online (§4.4) / возврата на экран (VIS-1)
    }
    /** Останавливает: закрывает сокет, гасит таймеры/подписки. Очередь персистится отдельно. SPEC §4.1 stop. */
    stop() {
        this.stopped = true;
        this.clearReconnect();
        this.clearConnectDeadline();
        this.offOnline?.();
        this.offOffline?.();
        this.offVisible?.();
        this.offOnline = null;
        this.offOffline = null;
        this.offVisible = null;
        this.teardownSocket();
        this.setStatus('closed');
    }
    close() {
        this.stop();
    }
    /** Готов ли слать durable-исходящие: сокет открыт И ready (для Outbox.canSend через фабрику). */
    isReady() {
        return this.ready && this.ws !== null && this.ws.readyState === WS_OPEN;
    }
    setCursors(cursors) {
        this.cursors = [...cursors];
    }
    /**
     * Шлёт команду серверу. До `ready` — буферит (subscribe/sync/read/typing). После — сериализует и
     * отправляет. subscribe с chatIds запоминается для пере-подписки на reconnect. SPEC §4.3.
     */
    send(cmd) {
        if (cmd.t === 'subscribe')
            this.subscribeChatIds = cmd.chatIds;
        if (!this.ready || this.ws === null || this.ws.readyState !== WS_OPEN) {
            this.preReadyBuffer.push(cmd);
            return;
        }
        this.rawSend(cmd);
    }
    // ── соединение ──────────────────────────────────────────────────────────────
    open() {
        if (this.opening)
            return;
        this.opening = true;
        void this.openAsync().finally(() => {
            this.opening = false;
        });
    }
    /**
     * Неудача установления соединения (дедлайн/обрыв до ready) — следующий кандидат по кругу
     * (мульти-edge: DPI провайдера может душить WS к конкретному edge при живом HTTP,
     * client-diag 2026-07-05). Успешный ready фиксирует текущего (sticky).
     */
    advanceEndpoint() {
        if (this.candidates.length > 1) {
            this.endpointIdx = (this.endpointIdx + 1) % this.candidates.length;
        }
    }
    async openAsync() {
        this.ready = false;
        this.preReadyBuffer = [];
        this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
        const url = this.candidates[this.endpointIdx] ?? this.deps.url;
        // Кросс-origin кандидат (idx>0): host-only cookie не поедет — берём одноразовый тикет по REST
        // (он ходит: WS душат при живом HTTP). Тикет — в subprotocol, НЕ в URL (D-07).
        let protocols;
        if (this.endpointIdx > 0) {
            let ticket = null;
            try {
                ticket = (await this.deps.getWsTicket?.()) ?? null;
            }
            catch {
                ticket = null;
            }
            if (this.stopped)
                return;
            if (ticket === null) {
                // тикет не добыт (REST упал/порт не задан) — этот кандидат бесполезен, дальше по кругу
                this.advanceEndpoint();
                this.scheduleReconnect();
                return;
            }
            protocols = [`tc-ticket.${ticket}`];
        }
        let socket;
        try {
            // Хендшейк без токена в URL: primary — httpOnly-cookie (D-07, §4.3.1), фолбэк — тикет в subprotocol.
            socket = this.deps.wsFactory(url, protocols);
        }
        catch {
            this.advanceEndpoint();
            this.scheduleReconnect();
            return;
        }
        this.ws = socket;
        socket.onopen = () => {
            // TCP/WS открыт, но НЕ ready: ждём серверное { t:'ready' } и сверку версии (§4.3.2).
            // attempt НЕ сбрасываем здесь — только после ready (§4.4, против flapping).
        };
        socket.onmessage = (ev) => this.onMessage(ev.data);
        socket.onclose = (ev) => this.onClose(ev.code);
        socket.onerror = () => {
            // Ошибка сокета: фактический обрыв придёт через onclose; здесь не дублируем reconnect.
        };
        this.armConnectDeadline(socket);
    }
    /**
     * Дедлайн open→ready: зависшую попытку рубим и идём на следующую (новый сокет = новый
     * source-port — при эпизодической потере пакетов проходит там, где старая ждала бы TCP-ретрансмиты
     * до минуты; client-diag 2026-07-05). teardown снимает хэндлеры → onclose не придёт, reconnect
     * планируем сами.
     */
    armConnectDeadline(socket) {
        this.clearConnectDeadline();
        this.connectTimer = this.deps.setTimeoutFn(() => {
            this.connectTimer = null;
            if (this.stopped || this.ready || this.ws !== socket)
                return;
            this.teardownSocket();
            this.advanceEndpoint(); // зависший хендшейк = неудача кандидата → пробуем следующий edge
            this.scheduleReconnect();
        }, this.deps.connectTimeoutMs);
    }
    clearConnectDeadline() {
        if (this.connectTimer !== null) {
            this.deps.clearTimeoutFn(this.connectTimer);
            this.connectTimer = null;
        }
    }
    onMessage(data) {
        const event = this.parseServerEvent(data);
        if (event === null)
            return; // мусор/искажённый кадр — игнорируем (стор бы всё равно отверг)
        if (event.t === 'ready') {
            this.onReadyEvent(event.v);
            return;
        }
        // Дедуп явных дублей (§4.9); семантику всё равно сводит стор.
        if (this.dedup.admit(event)) {
            this.deps.onServerEvent(event);
        }
    }
    onReadyEvent(serverV) {
        this.clearConnectDeadline(); // соединение состоялось (в т.ч. для ветки outdated — сокет закроем сами)
        // Сверка мажора протокола (§4.3.2). v1: мажор = целое v; несовпадение → не работаем на чужом.
        if (majorOf(serverV) !== majorOf(WS_PROTOCOL_VERSION)) {
            this.setStatus('outdated');
            this.deps.onProtocolOutdated(serverV, WS_PROTOCOL_VERSION);
            // Закрываем сокет и НЕ крутим reconnect (бесполезен до перезагрузки кода).
            this.stopped = true;
            this.clearReconnect();
            this.teardownSocket();
            return;
        }
        // Версия ок → ready. Сбрасываем backoff-счётчик ТОЛЬКО здесь (§4.4).
        this.ready = true;
        this.attempt = 0;
        this.setStatus('open');
        // subscribe (§4.3.3) + sync-добор по курсорам (§4.3.4) — на первом И каждом reconnect.
        this.rawSend({ t: 'subscribe', chatIds: this.subscribeChatIds });
        this.rawSend({ t: 'sync', cursors: this.cursors });
        // Доотправляем команды, накопленные до ready (read/typing/повторный subscribe от стора).
        const buffered = this.preReadyBuffer.splice(0);
        for (const cmd of buffered)
            this.rawSend(cmd);
        // Сигнал фабрике: можно сливать очередь исходящих (reconnect→дослать, §4.6.4).
        this.deps.onReady();
    }
    onClose(code) {
        const diedBeforeReady = !this.ready;
        this.ready = false;
        this.clearConnectDeadline();
        this.teardownSocket();
        if (code === CLOSE_AUTH_REQUIRED) {
            if (this.endpointIdx > 0) {
                // 4401 на тикет-кандидате ≠ смерть сессии (просрочка/рестарт сервера съел one-shot тикет):
                // РЕАЛЬНО умершую сессию поймает REST при добыче следующего тикета (401 → auth.required
                // через http-engine). Здесь — обычная неудача кандидата: дальше по кругу.
                this.advanceEndpoint();
                if (!this.stopped)
                    this.scheduleReconnect();
                return;
            }
            // Сессия невалидна (§4.9, primary/cookie): auth.required, без агрессивного reconnect.
            this.stopped = true;
            this.clearReconnect();
            this.setStatus('closed');
            this.deps.onAuthRequired();
            return;
        }
        // Соединение умерло, не дойдя до ready — неудача кандидата → следующий edge. Обрыв ПОСЛЕ
        // ready (iOS-фон и т.п.) кандидата не меняет: этот edge работал.
        if (diedBeforeReady)
            this.advanceEndpoint();
        if (this.stopped) {
            this.setStatus('closed');
            return;
        }
        // Обычный обрыв → reconnect с backoff (§4.4). Если офлайн — ждём online, не крутим таймер.
        if (!this.deps.network.isOnline()) {
            this.setStatus('reconnecting');
            return;
        }
        this.scheduleReconnect();
    }
    // ── reconnect ────────────────────────────────────────────────────────────────
    scheduleReconnect() {
        if (this.stopped)
            return;
        this.clearReconnect();
        this.setStatus('reconnecting');
        // VIS-1: приложение свёрнуто — таймер не крутим (в фоне попытки душит браузер, а батарея
        // тратится); возврат на экран поднимет соединение сразу (onPageVisible). attempt не растёт.
        if (!this.isPageVisible())
            return;
        const delay = backoffDelay(this.deps.backoff, this.attempt, this.deps.rand);
        this.attempt += 1;
        this.reconnectTimer = this.deps.setTimeoutFn(() => {
            this.reconnectTimer = null;
            if (this.stopped)
                return;
            if (!this.deps.network.isOnline())
                return; // online-событие поднимет
            if (!this.isPageVisible())
                return; // возврат на экран поднимет (VIS-1)
            this.open();
        }, delay);
    }
    clearReconnect() {
        if (this.reconnectTimer !== null) {
            this.deps.clearTimeoutFn(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    onNetworkOnline() {
        if (this.stopped)
            return;
        // Возврат сети → немедленная попытка, не ждём текущий таймер (§4.4). Свёрнуто — ждём экрана.
        this.clearReconnect();
        if (!this.isPageVisible()) {
            if (this.ws === null)
                this.setStatus('reconnecting');
            return;
        }
        if (this.ws === null)
            this.open();
    }
    /** VIS-1: возврат на экран → немедленная попытка, если соединения нет (и сеть есть). */
    onPageVisible() {
        if (this.stopped)
            return;
        if (this.ws !== null || this.opening)
            return; // соединение есть/устанавливается — не трогаем
        if (!this.deps.network.isOnline())
            return; // online-событие поднимет
        this.clearReconnect();
        this.open();
    }
    isPageVisible() {
        return this.deps.visibility?.isVisible() ?? true;
    }
    onNetworkOffline() {
        if (this.stopped)
            return;
        // Пропажа сети → не крутим таймер впустую; текущий сокет (если есть) сам закроется/oнclose.
        this.clearReconnect();
        this.setStatus('reconnecting');
    }
    // ── низкоуровневое ────────────────────────────────────────────────────────────
    rawSend(cmd) {
        if (this.ws === null || this.ws.readyState !== WS_OPEN)
            return;
        try {
            this.ws.send(JSON.stringify(cmd));
        }
        catch {
            // обрыв в момент send — onclose догонит и поднимет reconnect.
        }
    }
    teardownSocket() {
        const ws = this.ws;
        if (ws === null)
            return;
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        this.ws = null;
        try {
            ws.close();
        }
        catch {
            // уже закрыт — ничего.
        }
    }
    setStatus(status) {
        if (this._status === status)
            return;
        this._status = status;
        this.deps.onStatus(status);
    }
    /** Разбирает кадр в ServerEvent (минимальная валидация union по `t`). Мусор → null. */
    parseServerEvent(data) {
        let obj = data;
        if (typeof data === 'string') {
            try {
                obj = JSON.parse(data);
            }
            catch {
                return null;
            }
        }
        if (typeof obj !== 'object' || obj === null)
            return null;
        const t = obj.t;
        if (typeof t !== 'string' || !SERVER_EVENT_TYPES.has(t))
            return null;
        return obj;
    }
}
/** Допустимые значения дискриминатора ServerEvent (из shared-contracts WS-протокола §4.7). */
const SERVER_EVENT_TYPES = new Set([
    'ready',
    'message.new',
    'message.edited',
    'message.deleted',
    'reaction.set',
    'receipt.delivered',
    'receipt.read',
    'presence.update',
    'typing',
    'transcript.ready',
    'transcript.failed',
    'chat.updated',
    'chat.deleted',
    'unread.update',
    'ack',
    'sync.delta',
    'error',
]);
/** Мажорная часть версии протокола. v1: целое = мажор (расширение схемы — R2 на shared-contracts). */
function majorOf(v) {
    return Math.trunc(v);
}
//# sourceMappingURL=realtime-client.js.map